import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsService } from '@/services/analyticsService';

export type SubscriptionBannerLocation = 'dashboard' | 'learning_path' | 'library' | 'profile';

/**
 * Empty subscription banner shell.
 *
 * The content will be added once the final title, CTA and icon treatment are
 * decided. Its surface intentionally matches the Continue Reading card.
 */
const HeroPromoBanner = ({
  location,
  buttonColor,
  bookTitle,
}: {
  location: SubscriptionBannerLocation;
  buttonColor?: string;
  bookTitle?: string;
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    void analyticsService.logEvent('view_subscription_banner', {
      banner_location: location,
    });
  }, [location]);

  const handleClick = () => {
    void analyticsService.logEvent('click_subscription_banner', {
      banner_location: location,
    });
    navigate('/premium-subscription');
  };

  return (
    <div
      className="group relative flex h-[200px] w-full cursor-pointer flex-col overflow-hidden rounded-3xl p-0 ring-1 ring-black/10 transition-transform duration-150 ease-out active:scale-[0.99] before:pointer-events-none before:absolute before:left-1/2 before:top-1/2 before:z-[1] before:h-0 before:w-0 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-black/10 before:content-[''] before:transition-all before:duration-300 active:before:h-[180%] active:before:w-[180%]"
      style={{
        backgroundColor: '#FFFDFC',
        boxShadow: '0 14px 34px rgba(15, 23, 42, 0.22), 0 5px 12px rgba(15, 23, 42, 0.14)',
      }}
      dir="rtl"
      aria-label="بنر اشتراک بوکا"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
    >
      <div
        className={`relative z-10 flex min-h-0 flex-1 ${
          location === 'learning_path' ? 'items-center' : 'items-start pt-4'
        }`}
      >
        {location !== 'learning_path' && (
          <img
            src="/hero_character.webp"
            alt=""
            className="pointer-events-none absolute left-1 top-3 h-[90%] w-[55%] origin-bottom-left object-contain object-left-bottom"
          />
        )}

        <div
          className={`relative z-10 text-right ${
            location === 'learning_path'
              ? 'mr-4 w-[calc(100%-2rem)]'
              : 'mr-2 mt-1 w-[41%]'
          }`}
        >
          <h2 className="font-persian text-lg font-bold leading-snug text-black sm:text-xl">
            اشتراک نداری !
          </h2>
          {location === 'learning_path' ? (
            <p className="mt-2 font-persian text-xs leading-6 text-gray-700 sm:text-sm">
              با <strong>خرید اشتراک</strong> میتونی کتاب «{bookTitle || 'این کتاب'}» و کتاب‌های دیگه رو بخونی و از همه قابلیت‌های بوکا استفاده کنی
            </p>
          ) : (
            <p className="mt-2 font-persian text-[11px] leading-[1.65] text-gray-700 sm:text-xs">
              با <strong>خرید اشتراک</strong> بوکا به همه کتاب‌ها و قابلیت‌هاشون دسترسی پیدا میکنی
            </p>
          )}
        </div>
      </div>

      <button
        type="button"
        className="relative z-10 mx-5 mb-3 flex items-center justify-center gap-2 overflow-hidden rounded-2xl px-4 py-3.5 shadow-none transition-all duration-150 ease-out group-hover:brightness-110 active:scale-[0.98] before:pointer-events-none before:absolute before:left-1/2 before:top-1/2 before:z-0 before:h-0 before:w-0 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-white/30 before:content-[''] before:transition-all before:duration-300 active:before:h-[220%] active:before:w-[220%]"
        style={{ backgroundColor: buttonColor || '#7063FF', color: '#FFFFFF' }}
        onClick={(event) => {
          event.stopPropagation();
          handleClick();
        }}
      >
        <span className="relative z-10 text-sm font-bold font-persian sm:text-base">خرید اشتراک</span>
      </button>
    </div>
  );
};

export default HeroPromoBanner;
